import { useState, useRef, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
  FloatingPortal,
  arrow,
  FloatingArrow,
} from '@floating-ui/react'
import { useStyleState, type BorderStyle } from '../../stores/styleStore'
import { Button } from '../ui/button'
import { Icon } from '../ui/icon'

interface StyleToolPanelProps {
  activeMode: string
  activeToolId: string | null
}

const BORDER_STYLES: { key: BorderStyle; icon: string; label: string }[] = [
  { key: 'solid', icon: 'line', label: 'Solid' },
  { key: 'dashed', icon: 'horizontal', label: 'Dashed' },
  { key: 'dotted', icon: 'dots', label: 'Dotted' },
  { key: 'dashDot', icon: 'lineArrow', label: 'Dash-dot' },
]

const PREVIEW_COLORS = [
  '#000000', '#444444', '#666666', '#888888', '#AAAAAA', '#CCCCCC',
  '#FF0000', '#FF4500', '#FF8C00', '#FFD700', '#FFFF00', '#ADFF2F',
  '#00FF00', '#00FF7F', '#00CED1', '#1E90FF', '#0000FF', '#8A2BE2',
  '#9400D3', '#FF00FF', '#FF1493', '#DC143C', '#8B0000', '#FFFFFF',
]

function ColorPicker({ color, onChange }: { color: string; onChange: (c: string) => void }) {
  const [inputValue, setInputValue] = useState(color)
  
  useEffect(() => {
    setInputValue(color)
  }, [color])

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <input
          type="color"
          value={color}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <div
          className="w-8 h-8 rounded border border-border-default cursor-pointer"
          style={{ backgroundColor: color }}
        />
      </div>
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={() => {
          if (/^#[0-9A-Fa-f]{6}$/.test(inputValue)) {
            onChange(inputValue)
          } else {
            setInputValue(color)
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            if (/^#[0-9A-Fa-f]{6}$/.test(inputValue)) {
              onChange(inputValue)
            }
          }
        }}
        className="w-20 h-7 bg-bg-input text-fg-primary text-xs rounded border border-border-default px-2 font-mono outline-none focus:border-accent"
      />
    </div>
  )
}

function SliderControl({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (v: number) => void
  format?: (v: number) => string
}) {
  const displayValue = format ? format(value) : String(value)

  return (
    <div className="flex items-center gap-2">
      <span className="text-fg-muted text-xs w-12 flex-shrink-0">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 h-1.5 accent-accent cursor-pointer"
      />
      <span className="text-fg-primary text-xs w-10 text-right font-mono flex-shrink-0">{displayValue}</span>
    </div>
  )
}

export function StyleToolPanel({ activeMode: _activeMode, activeToolId }: StyleToolPanelProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  const {
    style,
    setFillColor,
    setStrokeColor,
    setStrokeWidth,
    setOpacity,
    setBorderStyle,
    rotateClockwise,
    rotateCounterClockwise,
    setFreeTextStyle,
    resetStyle,
  } = useStyleState()

  const showFillColor = useMemo(() => {
    if (!activeToolId) return false
    const shapesWithFill = new Set(['square', 'circle', 'rectangle', 'oval', 'polygon', 'redact'])
    const textTools = new Set(['freeText'])
    return shapesWithFill.has(activeToolId) || textTools.has(activeToolId)
  }, [activeToolId])

  const showStrokeColor = useMemo(() => {
    if (!activeToolId) return false
    return activeToolId !== 'stamp'
  }, [activeToolId])

  const showStrokeWidth = useMemo(() => {
    if (!activeToolId) return false
    const hasStrokeWidth = new Set(['ink', 'inkHighlighter', 'square', 'circle', 'rectangle', 'oval', 'line', 'lineArrow', 'polygon', 'polyline', 'redact', 'freeText'])
    return hasStrokeWidth.has(activeToolId)
  }, [activeToolId])

  const showBorderStyle = useMemo(() => {
    if (!activeToolId) return false
    const hasBorderStyle = new Set(['square', 'circle', 'rectangle', 'oval', 'line', 'lineArrow', 'polygon', 'polyline', 'redact'])
    return hasBorderStyle.has(activeToolId)
  }, [activeToolId])

  const showOpacity = useMemo(() => {
    return activeToolId !== null
  }, [activeToolId])

  const showRotation = useMemo(() => {
    if (!activeToolId) return false
    const hasRotation = new Set(['freeText', 'stamp', 'square', 'circle', 'rectangle', 'oval', 'line', 'lineArrow', 'polygon', 'polyline', 'redact'])
    return hasRotation.has(activeToolId)
  }, [activeToolId])

  const showTextStyle = useMemo(() => {
    if (!activeToolId) return false
    return activeToolId === 'freeText'
  }, [activeToolId])

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip({ fallbackAxisSideDirection: 'start' }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

  const showAnyControls = showFillColor || showStrokeColor || showStrokeWidth || showBorderStyle || showOpacity || showRotation || showTextStyle

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} className="inline-flex">
        <Button
          active={isOpen}
          className={isOpen ? '' : 'text-fg-secondary hover:text-fg-primary'}
        >
          <Icon icon="palette" size={18} />
        </Button>
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[9999] bg-bg-surface border border-border-default rounded-xl shadow-2xl p-3 min-w-[260px]"
            onClick={(e) => e.stopPropagation()}
          >
            {!showAnyControls && activeToolId && (
              <div className="text-fg-muted text-xs text-center py-4">
                {t('style.noStyleForTool') || 'No style options for this tool'}
              </div>
            )}

            {!activeToolId && (
              <div className="space-y-3">
                <div className="text-fg-muted text-xs text-center py-2">
                  {t('style.selectToolFirst') || 'Select a tool to customize styles'}
                </div>
              </div>
            )}

            {activeToolId && (
              <div className="space-y-3">
                {showStrokeColor && (
                  <div>
                    <span className="text-fg-muted text-xs block mb-1.5">{t('style.strokeColor') || 'Stroke Color'}</span>
                    <ColorPicker color={style.strokeColor} onChange={setStrokeColor} />
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {PREVIEW_COLORS.slice(0, 12).map((c) => (
                        <button
                          key={c}
                          onClick={() => setStrokeColor(c)}
                          className={`w-4 h-4 rounded-full border hover:scale-125 transition-transform ${style.strokeColor === c ? 'ring-2 ring-accent ring-offset-1 ring-offset-bg-surface' : 'border-border-default'}`}
                          style={{ backgroundColor: c }}
                          title={c}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {showFillColor && (
                  <div>
                    <span className="text-fg-muted text-xs block mb-1.5">{t('style.fillColor') || 'Fill Color'}</span>
                    <ColorPicker color={style.fillColor} onChange={setFillColor} />
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {PREVIEW_COLORS.slice(0, 12).map((c) => (
                        <button
                          key={c}
                          onClick={() => setFillColor(c)}
                          className={`w-4 h-4 rounded-full border hover:scale-125 transition-transform ${style.fillColor === c ? 'ring-2 ring-accent ring-offset-1 ring-offset-bg-surface' : 'border-border-default'}`}
                          style={{ backgroundColor: c }}
                          title={c}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {showOpacity && (
                  <div>
                    <SliderControl
                      label={t('style.opacity') || 'Opacity'}
                      value={style.opacity}
                      min={0.05}
                      max={1}
                      step={0.05}
                      onChange={setOpacity}
                      format={(v) => `${Math.round(v * 100)}%`}
                    />
                  </div>
                )}

                {showStrokeWidth && (
                  <div>
                    <SliderControl
                      label={t('style.strokeWidth') || 'Width'}
                      value={style.strokeWidth}
                      min={0.5}
                      max={30}
                      step={0.5}
                      onChange={setStrokeWidth}
                      format={(v) => `${v}px`}
                    />
                  </div>
                )}

                {showBorderStyle && (
                  <div>
                    <span className="text-fg-muted text-xs block mb-1.5">{t('style.borderStyle') || 'Border Style'}</span>
                    <div className="flex gap-1">
                      {BORDER_STYLES.map((bs) => (
                        <button
                          key={bs.key}
                          onClick={() => setBorderStyle(bs.key)}
                          className={`flex-1 h-8 flex items-center justify-center rounded border text-xs transition-all ${
                            style.borderStyle === bs.key
                              ? 'bg-accent/10 text-accent border-accent'
                              : 'border-border-default text-fg-secondary hover:text-fg-primary hover:border-border-strong'
                          }`}
                          title={bs.label}
                        >
                          <Icon icon={bs.icon} size={14} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {showTextStyle && (
                  <div className="space-y-3 pt-1 border-t border-border-default">
                    <span className="text-fg-muted text-xs block -mb-2">{t('style.textStyle') || 'Text Style'}</span>

                    <div>
                      <span className="text-fg-muted text-xs block mb-1">{t('style.fontFamily') || 'Font'}</span>
                      <select
                        value={style.freeTextStyle.fontFamily}
                        onChange={(e) => setFreeTextStyle({ fontFamily: e.target.value })}
                        className="w-full h-7 bg-bg-input text-fg-primary text-xs rounded border border-border-default px-2 outline-none focus:border-accent"
                      >
                        <option value="Arial">Arial</option>
                        <option value="Helvetica">Helvetica</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Courier New">Courier New</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Verdana">Verdana</option>
                      </select>
                    </div>

                    <div>
                      <SliderControl
                        label={t('style.fontSize') || 'Size'}
                        value={style.freeTextStyle.fontSize}
                        min={6}
                        max={72}
                        step={1}
                        onChange={(v) => setFreeTextStyle({ fontSize: v })}
                        format={(v) => `${v}pt`}
                      />
                    </div>

                    <div>
                      <span className="text-fg-muted text-xs block mb-1">{t('style.fontColor') || 'Font Color'}</span>
                      <ColorPicker color={style.freeTextStyle.fontColor} onChange={(c) => setFreeTextStyle({ fontColor: c })} />
                    </div>

                    <div>
                      <span className="text-fg-muted text-xs block mb-1.5">{t('style.textAlign') || 'Align'}</span>
                      <div className="flex gap-1">
                        {(['left', 'center', 'right'] as const).map((a) => (
                          <button
                            key={a}
                            onClick={() => setFreeTextStyle({ textAlign: a })}
                            className={`flex-1 h-7 flex items-center justify-center rounded border text-xs transition-all ${
                              style.freeTextStyle.textAlign === a
                                ? 'bg-accent/10 text-accent border-accent'
                                : 'border-border-default text-fg-secondary hover:text-fg-primary'
                            }`}
                          >
                            <Icon icon={`align${a.charAt(0).toUpperCase() + a.slice(1)}`} size={14} />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <span className="text-fg-muted text-xs block mb-1.5">{t('style.verticalAlign') || 'Vertical'}</span>
                      <div className="flex gap-1">
                        {(['top', 'middle', 'bottom'] as const).map((a) => (
                          <button
                            key={a}
                            onClick={() => setFreeTextStyle({ verticalAlign: a })}
                            className={`flex-1 h-7 flex items-center justify-center rounded border text-xs transition-all ${
                              style.freeTextStyle.verticalAlign === a
                                ? 'bg-accent/10 text-accent border-accent'
                                : 'border-border-default text-fg-secondary hover:text-fg-primary'
                            }`}
                          >
                            <Icon icon={`align${a.charAt(0).toUpperCase() + a.slice(1)}`} size={14} />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {showRotation && (
                  <div className="pt-1 border-t border-border-default">
                    <span className="text-fg-muted text-xs block mb-1.5">{t('style.rotation') || 'Rotation'}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={rotateCounterClockwise}
                        className="h-8 w-8 flex items-center justify-center rounded border border-border-default text-fg-secondary hover:text-fg-primary hover:border-border-strong transition-all"
                        title={t('style.rotateCounterClockwise') || 'Rotate Counter-Clockwise'}
                      >
                        <Icon icon="rotateCounterClockwise" size={16} />
                      </button>
                      <span className="flex-1 text-center text-xs font-mono text-fg-primary bg-bg-input rounded py-1">
                        {style.rotation}°
                      </span>
                      <button
                        onClick={rotateClockwise}
                        className="h-8 w-8 flex items-center justify-center rounded border border-border-default text-fg-secondary hover:text-fg-primary hover:border-border-strong transition-all"
                        title={t('style.rotateClockwise') || 'Rotate Clockwise'}
                      >
                        <Icon icon="rotateClockwise" size={16} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="pt-1 border-t border-border-default">
                  <button
                    onClick={resetStyle}
                    className="w-full text-xs text-fg-muted hover:text-fg-primary py-1 transition-colors"
                  >
                    {t('style.resetDefaults') || 'Reset to defaults'}
                  </button>
                </div>
              </div>
            )}

            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill="var(--ep-background-surface, #161922)"
              stroke="var(--ep-border-default, #94a2ba)"
              strokeWidth={1}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
